class PlugMobileMenu extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.elements = {
            announcementBar: document.querySelector(".announcement-bar-section"),
            backButtons: Array.from(document.getElementsByClassName("plug-mobile-menu__back")),
            drawerTrigger: document.querySelector(".plug-header__menu-button"),
            l1ItemsWithSubmenus: Array.from(this.querySelectorAll(".plug-mobile-menu__item.has-submenu")),
            mainMenu: this.querySelector(".plug-mobile-menu__menu-wrapper:not(.is-submenu)"),
            overlay: this.querySelector(".plug-mobile-menu__overlay"),
            searchTrigger: document.querySelector(".header__search"),
        };

        this.setAnnouncementBarHeight();
        this.removeListeners = this.initListeners();
    }

    setAnnouncementBarHeight() {
        if (this.elements.announcementBar) {
            document.documentElement.style.setProperty("--announcement-bar-height", `${this.elements.announcementBar.offsetHeight}px`);
        }
    }

    initListeners() {
        let resizeTimeout;

        const onDrawerTriggerClick = (event) => {
            const button = event.currentTarget;
            if (button.dataset.isExpanded === "true") {
                this.close();
            } else {
                this.open();
            }
        };

        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.setAnnouncementBarHeight();
            }, 200);
        };

        const onL1ButtonClick = (event) => {
            const button = event.currentTarget;
            const li = button.closest("li");
            this.openSubmenu(li);
        };

        const onBackButtonClick = (event) => {
            const button = event.currentTarget;
            const submenu = button.closest(".is-submenu");
            this.closeSubmenu(submenu);
        };

        const onSearch = () => {
            if (this.dataset.isOpen === "true") {
                this.close();
            }
        };

        const onOverlayClick = () => {
            this.close();
        };

        const onMenuKeydown = (e) => {
            const menu = e.currentTarget;
            if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Tab") {
                e.preventDefault();
                const activeElement = document.activeElement;
                const activeMenu = (
                    activeElement.closest(".plug-mobile-menu__menu-wrapper.is-submenu")
                    ? activeElement.closest(".plug-mobile-menu__menu-wrapper.is-submenu")
                    : this.querySelector(".plug-mobile-menu__menu-wrapper:not(.is-submenu)")
                );
                const focusableElements = Array.from(
                    activeMenu.querySelectorAll("a, button")
                ).filter(el => !el.closest(".is-hidden"));

                const index = focusableElements.indexOf(activeElement);
                if (index >= 0) {
                    if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
                        if (index < focusableElements.length - 1) {
                            focusableElements[index + 1].focus();
                        } else {
                            focusableElements[0].focus(); // Loop to first element
                        }
                    } else {
                        if (index > 0) {
                            focusableElements[index - 1].focus();
                        } else {
                            focusableElements[focusableElements.length - 1].focus(); // Loop to first element
                        }
                    }
                }
            } else if (e.key === "Escape") {
                e.preventDefault();
                const activeSubmenu = this.querySelector(".plug-mobile-menu__menu-wrapper.is-submenu[data-is-open='true']");
                if (activeSubmenu) {
                    this.closeSubmenu(activeSubmenu);
                }
                this.close();
            }
        };

        if (this.elements.drawerTrigger) {
            this.elements.drawerTrigger.addEventListener("click", onDrawerTriggerClick, false);
        }

        if (this.elements.mainMenu) {
            this.elements.mainMenu.addEventListener("keydown", onMenuKeydown, false);
        }

        if (this.elements.overlay) {
            this.elements.overlay.addEventListener("click", onOverlayClick, false);
        }

        if (this.elements.searchTrigger) {
            this.elements.searchTrigger.addEventListener("click", onSearch, false);
        }

        window.addEventListener("resize", handleResize, false);

        this.elements.l1ItemsWithSubmenus.forEach(li => {
            const l1Button = li.querySelector("button.plug-mobile-menu__link");
            if (l1Button) {
                l1Button.addEventListener("click", onL1ButtonClick, false);
            }
            const submenu = this.querySelector(`.is-submenu[data-index="${li.dataset.index}"]`);
            if (submenu) {
                submenu.addEventListener("keydown", onMenuKeydown, false);
            }
        });

        this.elements.backButtons.forEach(button => {
            button.addEventListener("click", onBackButtonClick, false);
        });

        return () => {
            if (this.elements.drawerTrigger) {
                this.elements.drawerTrigger.removeEventListener("click", onDrawerTriggerClick, false);
            }

            if (this.elements.mainMenu) {
                this.elements.mainMenu.removeEventListener("keydown", onMenuKeydown, false);
            }

            if (this.elements.overlay) {
                this.elements.overlay.removeEventListener("click", onOverlayClick, false);
            }

            if (this.elements.searchTrigger) {
                this.elements.searchTrigger.removeEventListener("click", onSearch, false);
            }

            window.removeEventListener("resize", handleResize, false);

            this.elements.l1ItemsWithSubmenus.forEach(li => {
                const l1Button = li.querySelector("button.plug-mobile-menu__link");
                if (l1Button) {
                    l1Button.removeEventListener("click", onL1ButtonClick, false);
                }
                const submenu = this.querySelector(`.is-submenu[data-index="${li.dataset.index}"]`);
                if (submenu) {
                    submenu.removeEventListener("keydown", onMenuKeydown, false);
                }
            });

            this.elements.backButtons.forEach(button => {
                button.removeEventListener("click", onBackButtonClick, false);
            });
        };
    }

    disconnectedCallback() {
        if (typeof this.removeListeners === "function") {
            this.removeListeners();
        }
    }

    open() {
        this.elements.drawerTrigger.dataset.isExpanded = "true";
        document.body.classList.add("plug-no-scroll");

        this.dataset.isOpen = "true";

        requestAnimationFrame(() => {
            setTimeout(() => {
                this.dataset.isVisible = "true";
            }, 0);
        });
    }

    close() {
        this.elements.drawerTrigger.dataset.isExpanded = "false";
        document.body.classList.remove("plug-no-scroll");
        this.dataset.isVisible = "false";

        setTimeout(() => {
            this.dataset.isOpen = "false";
        }, 300);
    }

    openSubmenu(li) {
        const index = li.dataset.index;
        const submenu = this.querySelector(`.is-submenu[data-index="${index}"]`);
        submenu.dataset.isOpen = "true";

        requestAnimationFrame(() => {
            setTimeout(() => {
                submenu.dataset.isVisible = "true";
            }, 0);
        });
    }

    closeSubmenu(submenu) {
        submenu.dataset.isVisible = "false";

        setTimeout(() => {
            submenu.dataset.isOpen = "false";
        }, 300);
    }
}

if (!customElements.get("plug-mobile-menu")) {
    customElements.define("plug-mobile-menu", PlugMobileMenu);
}